/** 贴纸坐标空间。这一版固定 512，写进 config.canvas。 */
export const STICKER_SIZE = 512

export const DOT_KEYS = ['L', 'R', 'M'] as const
export type DotKey = (typeof DOT_KEYS)[number]

const DOT_RGB: Record<DotKey, [number, number, number]> = {
  L: [255, 0, 0],
  R: [0, 0, 255],
  M: [0, 255, 0],
}

export type Dot = { x: number; y: number }
export type Face = { tx: number; ty: number; scale: number; rotation: number }
export type Raster = HTMLImageElement | HTMLCanvasElement

export type FitResult = {
  face: Face | null
  error: string | null
  warn: string | null
}

const round = (value: number, digits: number) =>
  Math.round(value * 10 ** digits) / 10 ** digits || 0

export function rasterSize(source: Raster) {
  if (source instanceof HTMLImageElement) {
    return { w: source.naturalWidth, h: source.naturalHeight }
  }
  return { w: source.width, h: source.height }
}

export function readPixels(source: Raster) {
  const { w, h } = rasterSize(source)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('无法读取图片')
  ctx.drawImage(source, 0, 0)
  return { w, h, data: ctx.getImageData(0, 0, w, h).data }
}

function classify(r: number, g: number, b: number, a: number): DotKey | null {
  if (a < 200) return null
  if (r > 200 && g < 50 && b < 50) return 'L'
  if (b > 200 && r < 50 && g < 50) return 'R'
  if (g > 200 && r < 50 && b < 50) return 'M'
  return null
}

export function findDots(data: Uint8ClampedArray, width: number) {
  const acc: Record<DotKey, [number, number, number]> = {
    L: [0, 0, 0],
    R: [0, 0, 0],
    M: [0, 0, 0],
  }
  const count = data.length / 4
  for (let i = 0; i < count; i++) {
    const key = classify(data[i * 4], data[i * 4 + 1], data[i * 4 + 2], data[i * 4 + 3])
    if (!key) continue
    acc[key][0] += (i % width) + 0.5
    acc[key][1] += ((i / width) | 0) + 0.5
    acc[key][2]++
  }
  const dots = {} as Record<DotKey, Dot | null>
  for (const key of DOT_KEYS) {
    dots[key] = acc[key][2]
      ? { x: acc[key][0] / acc[key][2], y: acc[key][1] / acc[key][2] }
      : null
  }
  return dots
}

/** 在脸图上按整数像素画三个色点，避免浏览器抗锯齿把质心画偏。 */
export function drawDotsExact(
  canvas: HTMLCanvasElement,
  anchors: Record<DotKey, Dot>,
) {
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('无法绘制色点')
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = image.data
  for (const key of DOT_KEYS) {
    const { x: cx, y: cy } = anchors[key]
    for (let y = Math.floor(cy - 9); y <= Math.ceil(cy + 9); y++) {
      for (let x = Math.floor(cx - 9); x <= Math.ceil(cx + 9); x++) {
        const r2 = (x + 0.5 - cx) ** 2 + (y + 0.5 - cy) ** 2
        if (r2 > 64 || x < 0 || y < 0 || x >= canvas.width || y >= canvas.height) continue
        const i = (y * canvas.width + x) * 4
        const rgb = r2 <= 25 ? DOT_RGB[key] : [255, 255, 255]
        data[i] = rgb[0]
        data[i + 1] = rgb[1]
        data[i + 2] = rgb[2]
        data[i + 3] = 255
      }
    }
  }
  ctx.putImageData(image, 0, 0)
}

export function buildKitFromFace(face: HTMLCanvasElement, anchors: Record<DotKey, Dot>) {
  if (face.width !== STICKER_SIZE || face.height !== STICKER_SIZE) {
    throw new Error('标准脸需要 512×512')
  }
  const { data } = readPixels(face)
  if (!data.some((value, index) => index % 4 === 3 && value > 16)) {
    throw new Error('画布上没有五官')
  }
  if (DOT_KEYS.some(key => {
    const point = anchors[key]
    return !point || !Number.isFinite(point.x) || !Number.isFinite(point.y) ||
      point.x < 0 || point.y < 0 || point.x >= face.width || point.y >= face.height
  })) throw new Error('色点坐标超出画布')
  const [a, b, c] = DOT_KEYS.map((key) => anchors[key])
  const area =
    Math.abs((b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y)) / 2
  if (area <= 200) throw new Error('三个色点几乎在一条直线上')

  let colored = 0
  for (let i = 0; i < data.length / 4; i++) {
    if (classify(data[i * 4], data[i * 4 + 1], data[i * 4 + 2], data[i * 4 + 3])) {
      colored++
    }
  }
  if (colored > 0) throw new Error('五官图里已有纯红、纯蓝或纯绿，会和色点混在一起')

  const kit = document.createElement('canvas')
  kit.width = face.width
  kit.height = face.height
  kit.getContext('2d')?.drawImage(face, 0, 0)
  drawDotsExact(kit, anchors)
  const pixels = readPixels(kit)
  const dots = findDots(pixels.data, pixels.w)
  if (DOT_KEYS.some((key) => !dots[key])) throw new Error('标准脸色点没有写上')
  return { kit, dots: dots as Record<DotKey, Dot> }
}

function solveAffine(src: Dot[], dst: Dot[]) {
  const [p, q, r] = src
  const det = p.x * (q.y - r.y) - p.y * (q.x - r.x) + (q.x * r.y - r.x * q.y)
  if (Math.abs(det) < 1e-6) return null
  const solve = (u: number, v: number, w: number) => [
    (u * (q.y - r.y) - p.y * (v - w) + (v * r.y - w * q.y)) / det,
    (p.x * (v - w) - u * (q.x - r.x) + (q.x * w - r.x * v)) / det,
    (p.x * (q.y * w - r.y * v) - p.y * (q.x * w - r.x * v) + u * (q.x * r.y - r.x * q.y)) / det,
  ]
  const [a, c, e] = solve(dst[0].x, dst[1].x, dst[2].x)
  const [b, d, f] = solve(dst[0].y, dst[1].y, dst[2].y)
  return { a, b, c, d, e, f }
}

function fitSimilarity(src: Dot[], dst: Dot[]) {
  const n = src.length
  const ps = src.reduce((o, p) => ({ x: o.x + p.x / n, y: o.y + p.y / n }), { x: 0, y: 0 })
  const qs = dst.reduce((o, p) => ({ x: o.x + p.x / n, y: o.y + p.y / n }), { x: 0, y: 0 })
  let dot = 0
  let cross = 0
  let den = 0
  for (let i = 0; i < n; i++) {
    const ux = src[i].x - ps.x
    const uy = src[i].y - ps.y
    const vx = dst[i].x - qs.x
    const vy = dst[i].y - qs.y
    dot += ux * vx + uy * vy
    cross += ux * vy - uy * vx
    den += ux * ux + uy * uy
  }
  const a = dot / den
  const b = cross / den
  const e = qs.x - (a * ps.x - b * ps.y)
  const f = qs.y - (b * ps.x + a * ps.y)
  let residual = 0
  for (let i = 0; i < n; i++) {
    const dx = a * src[i].x - b * src[i].y + e - dst[i].x
    const dy = b * src[i].x + a * src[i].y + f - dst[i].y
    residual = Math.max(residual, Math.hypot(dx, dy))
  }
  return { a, b, e, f, residual }
}

export function fitFace(
  kitDots: Record<DotKey, Dot>,
  faceImage: Raster,
): FitResult {
  const { w, h } = rasterSize(faceImage)
  if (w !== STICKER_SIZE || h !== STICKER_SIZE) {
    return { face: null, error: `定位图是 ${w}×${h}，需要 512×512`, warn: null }
  }
  const pixels = readPixels(faceImage)
  const dots = findDots(pixels.data, pixels.w)
  const missing = DOT_KEYS.filter((key) => !dots[key])
  if (missing.length) {
    const name = { L: '左眼', R: '右眼', M: '嘴' }
    return {
      face: null,
      error: `定位图缺少色点：${missing.map((key) => name[key]).join('、')}`,
      warn: null,
    }
  }
  const src = DOT_KEYS.map((key) => kitDots[key])
  const dst = DOT_KEYS.map((key) => dots[key] as Dot)
  const affine = solveAffine(src, dst)
  if (!affine) return { face: null, error: '三个色点无法对齐', warn: null }
  const det = affine.a * affine.d - affine.b * affine.c
  if (det < 0) return { face: null, error: '定位图是镜像', warn: null }

  const sx = Math.hypot(affine.a, affine.b)
  const sy = Math.hypot(affine.c, affine.d)
  const shear = (affine.a * affine.c + affine.b * affine.d) / (sx * sy)
  if (Math.abs(sx / sy - 1) > 0.03 || Math.abs(shear) > 0.03) {
    return { face: null, error: '定位图不是等比缩放', warn: null }
  }

  const fitted = fitSimilarity(src, dst)
  if (fitted.residual > 1.5) {
    return {
      face: null,
      error: `色点对不齐（偏差 ${fitted.residual.toFixed(2)}px）`,
      warn: null,
    }
  }
  const scale = Math.hypot(fitted.a, fitted.b)
  let rotation = (Math.atan2(fitted.b, fitted.a) * 180) / Math.PI
  if (Math.abs(rotation) < 0.05) rotation = 0
  if (Math.abs(rotation) > 15) {
    return { face: null, error: `歪头 ${rotation.toFixed(1)}°，超过 ±15°`, warn: null }
  }
  if (scale < 0.5 || scale > 2) {
    return { face: null, error: `缩放 ${scale.toFixed(3)}，需要在 0.5 到 2 之间`, warn: null }
  }
  const warn =
    scale > 1.3 ? `缩放 ${scale.toFixed(3)} 偏大，请看预览` : null
  return {
    face: {
      tx: round(fitted.e, 2),
      ty: round(fitted.f, 2),
      scale: round(scale, 4),
      rotation: round(rotation, 2),
    },
    error: null,
    warn,
  }
}

/** 先按脸位画脸，再盖上挖洞贴纸。用的是写进 json 的那四个数。 */
export function compositeSticker(face: Raster, sticker: Raster, params: Face) {
  const canvas = document.createElement('canvas')
  canvas.width = STICKER_SIZE
  canvas.height = STICKER_SIZE
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('无法合成预览')
  const rad = (params.rotation * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const s = params.scale
  ctx.setTransform(s * cos, s * sin, -s * sin, s * cos, params.tx, params.ty)
  ctx.drawImage(face, 0, 0)
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.drawImage(sticker, 0, 0)
  return canvas
}
